import { debugError } from '@utils';

class ProductLookUp {
  static #version = '2025-07';
  #cache = new Map();

  static #queryProduct = (sku, scope) => `
    {
      predictiveSearch(query: "${sku}", searchableFields: [VARIANTS_SKU], limit: 10) {
        products {
          handle
          id
          title
          variants(first: 100, sortKey: POSITION) {
            nodes {
              id
              sku
              title
              availableForSale
              price {
                amount
                currencyCode
              }
              compareAtPrice {
                amount
                currencyCode
              }
              addOns: metafield(namespace: "add_on", key: "products") {
                key
                type
                entries: references(first: 100) {
                  nodes {
                    ... on Metaobject {
                      id
                      handle
                      fields {
                        key
                        type
                        value
                        images: references(first: 10) {
                          nodes {
                            ... on MediaImage {
                              id
                              image {
                                url
                                altText
                                width
                                height
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
              options: metafield(namespace: "add_on", key: "options") {
                key
                type
                entries: references(first: 100) {
                  nodes {
                    ... on Metaobject {
                      id
                      handle
                      fields {
                        key
                        type
                        value
                        images: references(first: 10) {
                          nodes {
                            ... on MediaImage {
                              id
                              image {
                                url
                                altText
                                width
                                height
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
              original_price: metafield(namespace: "${scope}cm_tier", key: "strike_price") {
                value
                key
                type
              }
            }
          }
        }
      }
    }
  `;

  /**
   * Find a variant by format and print material
   * @param {string} printMaterialSku (PM)
   * @param {string} formatSku (F)
   * @returns {string}
   */
  async find(printMaterialSku, formatSku) {
    if (!formatSku) return null;
    if (!printMaterialSku) return null;
    if (!window.theme.storefront_api_token) return null;

    // Format the variant SKU
    const sku = `${printMaterialSku}${formatSku}`;

    // Return cached result if available
    if (this.#cache.has(sku)) {
      return this.#cache.get(sku);
    }

    try {
      // Primary: predictiveSearch (fast, uses search index)
      const data = await this.#findByPredictiveSearch(sku);
      if (data) {
        this.#cache.set(sku, data);
        return data;
      }
    } catch (error) {
      debugError('predictiveSearch failed, trying fallback', error);
    }

    try {
      // Fallback: direct product query by SKU (slower but doesn't depend on search index)
      const data = await this.#findByDirectQuery(sku);
      if (data) {
        this.#cache.set(sku, data);
        return data;
      }
    } catch (error) {
      debugError('Direct query fallback also failed', error);
    }

    throw new Error(window.toast_translations.cart.no_product);
  }

  /**
   * Primary lookup via predictiveSearch (depends on search index)
   * @param {string} sku
   * @returns {Object|null}
   */
  async #findByPredictiveSearch(sku) {
    const query = ProductLookUp.#queryProduct(sku, window.theme.app_scope);

    const response = await fetch(`/api/${ProductLookUp.#version}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': window.theme.storefront_api_token,
      },
      body: JSON.stringify({ query }),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch product data');
    }
    const responseData = await response.json();

    const product = responseData.data.predictiveSearch.products?.[0];
    const variant = product?.variants?.nodes?.find((v) => v.sku === sku);
    const data = this.#formatData(variant, product);

    if (!data || !data?.id || !data?.product?.handle) {
      return null;
    }

    return data;
  }

  /**
   * Fallback lookup via products query with SKU filter (doesn't depend on search index)
   * @param {string} sku
   * @returns {Object|null}
   */
  async #findByDirectQuery(sku) {
    const query = `
      {
        products(first: 5, query: "variants.sku:${sku}") {
          nodes {
            handle
            id
            title
            variants(first: 100, sortKey: POSITION) {
              nodes {
                id
                sku
                title
                availableForSale
                price {
                  amount
                  currencyCode
                }
                compareAtPrice {
                  amount
                  currencyCode
                }
                addOns: metafield(namespace: "add_on", key: "products") {
                  key
                  type
                  entries: references(first: 100) {
                    nodes {
                      ... on Metaobject {
                        id
                        handle
                        fields {
                          key
                          type
                          value
                          images: references(first: 10) {
                            nodes {
                              ... on MediaImage {
                                id
                                image {
                                  url
                                  altText
                                  width
                                  height
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
                options: metafield(namespace: "add_on", key: "options") {
                  key
                  type
                  entries: references(first: 100) {
                    nodes {
                      ... on Metaobject {
                        id
                        handle
                        fields {
                          key
                          type
                          value
                          images: references(first: 10) {
                            nodes {
                              ... on MediaImage {
                                id
                                image {
                                  url
                                  altText
                                  width
                                  height
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
                original_price: metafield(namespace: "${window.theme.app_scope}cm_tier", key: "strike_price") {
                  value
                  key
                  type
                }
              }
            }
          }
        }
      }
    `;

    const response = await fetch(`/api/${ProductLookUp.#version}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': window.theme.storefront_api_token,
      },
      body: JSON.stringify({ query }),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch product data (fallback)');
    }
    const responseData = await response.json();

    // Find the product and variant with exact SKU match
    for (const product of responseData.data?.products?.nodes || []) {
      const variant = product.variants?.nodes?.find((v) => v.sku === sku);
      if (variant) {
        const data = this.#formatData(variant, product);
        if (data?.id && data?.product?.handle) {
          return data;
        }
      }
    }

    return null;
  }

  /**
   * Map the graphql response to a more readable format
   * @param {Object} variant
   * @param {Object} product
   * @returns {Object}
   */
  #formatData(variant, product) {
    if (!variant || !product) return null;

    // Format the data
    const data = {
      id: variant?.id,
      sku: variant?.sku,
      title: variant?.title,
      price: variant?.price,
      availableForSale: variant?.availableForSale,
      compareAtPrice: variant?.compareAtPrice,
      addOns: this.#formatAddOns(variant?.addOns?.entries?.nodes),
      options: this.#formatOptions(variant?.options?.entries?.nodes),
      original_price: JSON.parse(variant?.original_price?.value || '{}'),
      product: {
        id: product?.id,
        handle: product?.handle,
        title: product?.title,
      },
    };

    return data;
  }

  /**
   * Format the add-ons
   * @param {Object} addOns
   * @returns {Object}
   */
  #formatAddOns(addOns) {
    if (!addOns) return null;

    // Format the add-ons
    return addOns?.map((addOn) => {
      // Format the add-on metaobject
      const addOnData = {
        id: addOn?.id,
        handle: addOn?.handle,
        title: addOn?.title,
      };

      // Format the add-on fields
      addOn?.fields?.forEach((field) => {
        if (field?.type === 'list.file_reference' && field?.images?.nodes?.length > 0) {
          addOnData[field?.key] = this.#formatImages(field?.images?.nodes);
        } else {
          addOnData[field?.key] = field?.value;
        }
      });

      // Return the add-on data
      return addOnData;
    });
  }

  /**
   * Format the options
   * @param {Object} options
   * @returns {Object}
   */
  #formatOptions(options) {
    if (!options) return null;

    // Format the options
    return options?.map((option) => {
      // Format the option metaobject
      const optionData = {
        id: option?.id,
        handle: option?.handle,
        title: option?.title,
      };

      // Format the option fields
      option?.fields?.forEach((field) => {
        if (field?.type === 'list.file_reference' && field?.images?.nodes?.length > 0) {
          optionData[field?.key] = this.#formatImages(field?.images?.nodes);
        } else {
          optionData[field?.key] = field?.value;
        }
      });

      // Return the option data
      return optionData;
    });
  }

  /**
   * Format the images
   * @param {Object} images
   * @returns {Object}
   */
  #formatImages(images) {
    if (!images) return null;

    // Format the images
    return images?.map((data) => ({
      id: data?.id,
      url: data?.image?.url,
      altText: data?.image?.altText,
      width: data?.image?.width,
      height: data?.image?.height,
    }));
  }
}

const productLookUp = new ProductLookUp();
export { productLookUp };
